/**
 * Data layer for the COMPANY panel's Shoorah Pods section.
 *
 * Backed by the company/v1 pods-hub endpoints (token-scoped to the company;
 * the B2B/Members toggle is appended by api/companyApi.js). Every fetcher
 * takes an optional `department` so the section's filter can narrow the whole
 * page to one department.
 *
 * The adapters are exported because a future super-admin Pods Hub is served by
 * the same builders in services/podsHubService.js and should adapt the payload
 * through here rather than restating the field mapping.
 */

import {
  getCompanyPodsDashboard,
  getCompanyPodsUsers,
  getCompanyPodsUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Play-ledger block shared by every pods dashboard. */
export const adaptPodsListening = (data) => ({
  listeners: data.listeners,
  totalPlays: data.totalPlays,
  totalPlayTimeMins: data.totalPlayTimeMins,
  avgSessionMins: data.avgSessionMins,
  avgPlaysPerListener: data.avgPlaysPerListener,
  mostPlayedPod: data.mostPlayedPod,
  topTenSharePercentage: data.topTenSharePercentage,
  podsPlayed: data.podsPlayed ?? 0,
  mostPlayed: (data.mostPlayed || []).map((pod) => ({
    id: pod._id,
    name: pod.name,
    plays: pod.plays,
    listeners: pod.listeners ?? null,
    playTimeMins: pod.playTimeMins,
    rating: pod.rating ?? null,
    expert: pod.expert || null
  })),
  // Backend zero-fills the last 30 days; charts consume plain series.
  playsTrend: (data.playsTrend || []).map((day) => day.plays),
  listenMinsTrend: (data.playsTrend || []).map((day) => day.listenMins || 0),
  weekdayPlays: data.weekdayPlays || [],
  sessionLengthDistribution: data.sessionLengthDistribution || [],
  listenerEngagement: data.listenerEngagement || [],
  formatSplit: data.formatSplit || [],
  topExperts: data.topExperts || []
});

export const fetchCompanyPodsDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyPodsDashboard(department));
    return {
      ...adaptPodsListening(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees have listened".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyPodsDashboard error:', error);
    return null;
  }
};

// Matches the PodsUsersTable fetcher contract.
export const fetchCompanyPodsUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyPodsUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyPodsUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's pods profile ({ user, mostPlayedPod, topPods,
 * podHistory }). The backend 404s any user outside this company, so no scoping
 * is needed here.
 */
export const fetchCompanyPodsUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyPodsUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyPodsUserDetail error:', error);
    return null;
  }
};
